import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useNotifications} from "common/src/hooks/notification/notificationHook";
import PromiseBuilder from "common/src/utils/PromiseUtils";
import ApiClient from "common/src/api/ApiClient";
import {dateToLocalizedFormat} from "common/src/utils/DateUtils";
import {requestStatus} from "../../../model/requestStatus";
import GuestInput from "common/src/components/widget/GuestInput";
import IconLabel from "../../widget/IconLabel";
import {BoardType} from "../../../model/boardType";


function RequestDetailsPage() {
  const {addErrorNotification, addSuccessNotification} = useNotifications();
  const {requestId} = useParams();
  const [requestDetails, setRequestDetails] = useState(null);
  const {t} = useTranslation();
  const navigate = useNavigate();
  const requestStatusList = requestStatus();
  const boardTypes = BoardType();

  function deleteRequest() {
    new PromiseBuilder()
      .then(async () => ApiClient.put(`/api/v1/booking/request/delete/${requestId}`))
      .then(async (res) => {
        navigate("/request");
        return addSuccessNotification(t("toast.success", {code: res.code}), t("toast.success"));
      })
      .catch((res) => {
        return addErrorNotification(t("toast.error", {code: res.code}), t("toast.error"));
      })
      .run();
  }

  function processRequest() {
    new PromiseBuilder()
      .then(async () => ApiClient.put(`/api/v1/booking/request/process/${requestId}`))
      .then(async (res) => {
        navigate(`/offers/create/request/${requestId}`);
        return addSuccessNotification(t("toast.success", {code: res.code}), t("toast.success"));
      })
      .catch((res) => {
        return addErrorNotification(t("toast.error", {code: res.code}), t("toast.error"));
      })
      .run();
  }

  function undoRequestDeletion() {
    new PromiseBuilder()
      .then(async () => ApiClient.put(`/api/v1/booking/request/undo_deletion/${requestId}`))
      .then(async (res) => {
        navigate("/request");
        return addSuccessNotification(t("toast.success", {code: res.code}), t("toast.success"));
      })
      .catch((res) => {
        return addErrorNotification(t("toast.error", {code: res.code}), t("toast.error"));
      })
      .run();
  }

  useEffect(() => {
    if (!requestId) {
      return;
    }
    new PromiseBuilder()
      .then(async () => ApiClient.get(`/api/v1/booking/request/${requestId}`))
      .then(async (res) => {
        const data = res.data.payload;
        setRequestDetails(data);
      })
      .catch((res) => {
        return addErrorNotification(t("toast.fetch_failed_code", {code: res.code}), t("toast.error"));
      })
      .run();
  }, [])

  function getBoardType(id) {
    return boardTypes.filter(board => board.id === id)[0].name;
  }

  return (
    <div>
      <div className="columns is-widescreen mb-4">
        <div className="column is-9-widescreen">
          <div className="card is-full-height">
            <header className="card-header has-height-75">
              <p className="card-header-title">
                <span className="icon fz fz-sign-out-alt medium mr-1"/>
                <span>{t("request.request_data")}</span>
              </p>
            </header>
            <div className="card-content">
              <div className="columns">
                <div className="column is-3 info-card-right-border">
                  <IconLabel
                    label={ (requestDetails && requestDetails.startDate == null && requestDetails.endDate == null) ? "/" :
                    requestDetails && `${requestDetails.startDate ?? "/"} : ${requestDetails.endDate ?? "/"}`}
                    icon={"fz-calendar"}/>
                  <IconLabel
                    label={  (requestDetails && requestDetails.numberOfAdults == null && requestDetails.numberOfChildren == null) ? "/" :
                    requestDetails ? `${requestDetails.numberOfAdults ?? 0 + requestDetails.numberOfChildren ?? 0} (${t('request.adults')}: ${requestDetails.numberOfAdults ?? "/"}, ${t('request.children')}: ${requestDetails.numberOfChildren ?? "/"})` : ""}
                    icon={"fz-users"}/>
                  <IconLabel
                    label={requestDetails?.numberOfRooms != null ? `${requestDetails.numberOfRooms} ${t('request.rooms')}` : "/"}
                    icon={"fz-home"}/>
                  <IconLabel
                    label={requestDetails?.board != null ? `${getBoardType(requestDetails.board)}` :  t('request.no_board')}
                    icon={"fz-fork-knife"}/>
                </div>
                <div className="column is-3">

                  <IconLabel label={t('request.trail_service.guide')}
                             isCheckbox={true}
                             isChecked={requestDetails?.trailService.guide}/>
                  <IconLabel label={t('request.trail_service.back_transfer')}
                             isCheckbox={true}
                             isChecked={requestDetails?.trailService.backTransfer}/>
                  <IconLabel
                    label={t('request.trail_service.luggage_transport')}
                    isCheckbox={true}
                    isChecked={requestDetails?.trailService.luggageTransport}/>
                </div>
                <div className="column is-6">
                  <div className="info-card-wrapper">
                    <div
                      className="has-text-weight-bold">{t("request.deal")}:
                    </div>
                    <div>{requestDetails?.deal ?? "/"}</div>
                  </div>
                  <div className="info-card-wrapper">
                    <div
                      className="has-text-weight-bold">{t("request.note_from_client")}:
                    </div>
                    <div>{requestDetails?.noteFromClient ?? "/"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="column is-3-widescreen">
          <div className="card is-full-height">
            <header className="card-header has-height-75">
                    <p className="card-header-title">
                      <span className="icon fz fz-cog medium mr-1"/>
                      <span>{t("request.request_details")}</span>
                    </p>
                  <div className="is-right card-header-icon">
                    <div
                      className={"dropdown is-right is-hoverable"}>
                      <div className="dropdown-trigger">
                        <button className="button is-primary"
                                aria-haspopup="true"
                                aria-controls="dropdown-menu4">
                          <span className="fz fz-ellipsis"
                             aria-hidden="true"></span>
                        </button>
                      </div>
                      <div className="dropdown-menu">
                        <div className="dropdown-content pt-0">
                          { requestDetails?.status === "NEW" &&
                          <a className="dropdown-item"
                             onClick={() => deleteRequest()}>
                            {t("request.delete_request")}
                          </a>
                          }
                          { requestDetails?.status !== 'DELETED' &&
                            <>
                              <hr className="dropdown-divider"/>
                              <a className="dropdown-item"
                                 onClick={() => processRequest()}>
                                {t("request.process_request")}
                              </a>
                            </>
                          }
                          { requestDetails?.status === "DELETED" &&
                            <>
                              <hr className="dropdown-divider"/>
                              <a className="dropdown-item"
                                 onClick={() => undoRequestDeletion()}>
                                {t("request.undo_deletion")}
                              </a>
                            </>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
            </header>
            <div className="card-content">
              <div className="columns">
                <div className="column is-4">{t("request.created")}:</div>
                <div
                  className="column is-8">{requestDetails && dateToLocalizedFormat(requestDetails.dateCreated)}</div>
              </div>
              <div className="columns">
                <div className="column is-4">{t("status")}:</div>
                <div
                  className="column is-8">{requestDetails && requestStatusList.find(c => c.id === requestDetails.status).name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <header className="card-header">
          <p className="card-header-title">
            <span className="icon fz fz-bike medium mr-1"/>
            <span>{t("client")}</span>
          </p>
        </header>
        <div className="card-content">
          <GuestInput
            firstNameValue={requestDetails?.guest.name}
            lastNameValue={requestDetails?.guest.surname}
            mobileNumberValue={requestDetails?.guest.mobileNumber}
            emailValue={requestDetails?.guest.email}
            disabled={true}
          />
        </div>
      </div>
    </div>
  )
}

export default RequestDetailsPage;
